import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, Loader2, ImagePlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Multi-image uploader. Accepts:
 *   value: array of image URLs (existing)
 *   onChange: (newArray) => void
 *   max: max number of images (default 8)
 */
export const ImageUploader = ({ value = [], onChange, max = 8 }) => {
  const [uploading, setUploading] = useState(false);

  const buildAbs = (u) => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    if (u.startsWith('/api/')) return `${BACKEND_URL}${u}`;
    return u;
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = max - value.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${max} images`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    const uploaded = [];
    for (const f of toUpload) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const token = localStorage.getItem('admin_token');
        const res = await axios.post(`${API}/admin/upload-image`, fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        uploaded.push(res.data.url);
      } catch (err) {
        toast.error(`Failed: ${f.name} — ${err?.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    e.target.value = ''; // allow re-selecting same file
    if (uploaded.length) {
      onChange([...value, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length}`);
    }
  };

  const removeAt = (i) => {
    onChange(value.filter((_, idx) => idx !== i));
  };

  const moveLeft = (i) => {
    if (i === 0) return;
    const next = [...value];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {value.length}/{max} images · first is the main cover
        </span>
        <label className="cursor-pointer inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-border bg-muted hover:bg-accent hover:text-accent-foreground transition">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
          {uploading ? 'Uploading...' : 'Add images'}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
            disabled={uploading || value.length >= max}
          />
        </label>
      </div>

      {value.length > 0 ? (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
          {value.map((url, i) => (
            <div
              key={`${url}-${i}`}
              className={`relative aspect-square rounded-lg overflow-hidden border ${i === 0 ? 'border-accent' : 'border-border'} group`}
            >
              <img src={buildAbs(url)} alt={`#${i + 1}`} className="w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-accent text-accent-foreground font-semibold">
                  COVER
                </span>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
                {i > 0 && (
                  <button
                    onClick={() => moveLeft(i)}
                    className="text-xs text-white bg-accent/80 hover:bg-accent rounded px-2 py-1"
                    title="Move left / make cover"
                    type="button"
                  >
                    ←
                  </button>
                )}
                <button
                  onClick={() => removeAt(i)}
                  className="text-xs text-white bg-red-500/80 hover:bg-red-500 rounded p-1"
                  title="Remove"
                  type="button"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
          {value.length < max && (
            <label className="cursor-pointer aspect-square rounded-lg border-2 border-dashed border-border hover:border-accent transition flex items-center justify-center text-muted-foreground">
              <Upload className="w-5 h-5" />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFiles}
                className="hidden"
                disabled={uploading}
              />
            </label>
          )}
        </div>
      ) : (
        <label className="cursor-pointer block rounded-lg border-2 border-dashed border-border hover:border-accent transition p-8 text-center text-muted-foreground">
          <Upload className="w-6 h-6 mx-auto mb-2" />
          <p className="text-sm">Click to upload images</p>
          <p className="text-xs mt-1">JPG / PNG / WEBP · up to {max} · max 8MB each</p>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
            disabled={uploading}
          />
        </label>
      )}
    </div>
  );
};

export default ImageUploader;
